/* eslint-disable operator-linebreak */
/* eslint-disable max-len */
import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { FormSpy } from 'react-final-form';
import { FormApi, FormState } from 'final-form';
import { useHistory, useLocation } from 'react-router-dom';
import * as yup from 'yup';
import { CircularProgress, makeStyles } from '@material-ui/core';
import { getTransactionsArray } from 'utils/mapping/transactionCardMapper';
import api from 'api/api';
import routePaths from 'routes/routePaths';
import { OTPConfirmationModal, StatusModal, WarningModal } from 'components/modals';
import Form from 'ui/Form/Form';
import Button from 'ui/Button/Button';
import { AxiosStatus } from 'api/axiosConfig';
import FieldsLeveler from 'ui/structure/FieldsLeveler/FieldsLeveler';
import SubmitLeveler from 'ui/structure/SubmitLeveler/SubmitLeveler';
import { DEFAULT_ACCOUNT_TYPE, NETWORK_REQUIRED } from 'utils/constants';
import { requiredOnly, validationCallback, validateAmount, validateAccount } from 'utils/validation';
import { changeFieldValue, setFieldTouched } from 'utils/formMutators';
import { IAccount } from 'store/accounts.model';
import { IApiResponse, ITransferBeneficiary, ITransferBenLocationData } from 'utils/models';
import { IOTPError } from 'components/authModules/LogIn/login.model';
import validateOtpPin, { handlePinError } from 'utils/otpValidation';
import { lookupOtherAccount, getReceipt, getTransferCharge } from './submitApi';
import {
  IFormValues,
  ITransactionField,
  ITransferResponse,
  ITransfersState,
  IExtraFields,
  sortArrays,
  IAccountLookup,
  IGetTransferBeneficiaries,
  IGetBankInstitutions,
  IBanksMappedList,
  IGetNonInstantBanks,
  INonInstantBank,
  IBank,
  ITransferCharge,
  ITransferActions,
  BankTypeKeys,
  types,
  sortWithScheduleArrays,
  TransactionType,
  IInstantBank,
} from './ToAnotherBank.model';
import { renderTransferFieldsFrom, renderTransferFieldsTo } from './ToAnotherBankFields';

const warnings = {
  MAX_BULK_TRANSFERS: 'You have reached the maximum(10) number of transfers.',
  SAME_ACCOUNT: 'The destination account should not be same as source account.',
  ACCOUNT_NOT_VERIFIED: 'The destination account was not verified.',
  ENTER_NON_INSTANT_BEN: 'You should enter the beneficiary name.',
  INSUFFICIENT_FUNDS: 'You have insuffient balance for this transaction',
};

const schema = yup.object().shape({
  sourceAccount: requiredOnly('Source Account'),
  transferType: requiredOnly('Transfer Type'),
  amount: validateAmount('Amount'),
  destinationBankCode: requiredOnly('Bank').test('ben-name', 'Select a transfer type first', (value, context) => {
    const otherBankValues: IFormValues = context.parent as IFormValues;
    return !!otherBankValues?.transferType?.trim();
  }),
  destinationAccountNo: validateAccount('Destination Account'),
  beneficiaryName: yup.string().test('ben-name', 'Beneficiary Name is required', (value, context) => {
    const otherBankValues: IFormValues = context.parent as IFormValues;
    if (otherBankValues?.transferType === 'NON_INSTANT') {
      return !!value;
    }
    return true;
  }),
  narration: requiredOnly('Narration'),
  scheduleTransfer: yup.boolean(),
  frequency: yup.string().when('scheduleTransfer', {
    is: true,
    then: requiredOnly('Frequency'),
  }),
  startDate: yup.string().test('startDate', 'Special Date is required', (value, context) => {
    const FABValues: IFormValues = context.parent as IFormValues;
    if (FABValues?.frequency === 'specialDate') {
      return !!value;
    }
    return true;
  }),
});

interface IProps {
  accounts: IAccount[];
  handleChangeForm: () => void;
}

const INITIAL_EXTRAS: IExtraFields = {
  destinationBankName: '',
  destinationBankCode: '',
  destinationAccountName: '',
  destinationIsExistingBen: false,
  beneficiaryId: 0,
};

const useStyles = makeStyles({
  root: {
    display: 'flex',
    columnGap: '24px',
    margin: 0,
    paddingTop: 16,
  },
  label: {
    fontFamily: 'var(--ff-etelka-text)',
    fontSize: 14,
    lineHeight: '24px',
    '&.Mui-disabled': {
      color: '#000',
    },
  },
});

const ToAnotherBank = ({ accounts, handleChangeForm }: IProps) => {
  const [submitStatus, setSubmitStatus] = useState<AxiosStatus>(AxiosStatus.DEFAULT);
  const submitting = submitStatus === AxiosStatus.PENDING;
  const [OTPError, setOTPError] = useState<IOTPError | null>(null);

  const [state, setState] = useState<ITransfersState>({
    transferBeneficiaries: [],
    types,
    banks: [],
    extras: INITIAL_EXTRAS,
    lastLookedUpAccount: '',
    lastLookedUpBankCode: '',
  });
  const updateState = useCallback(
    (update: Partial<ITransfersState>) => setState((current) => ({ ...current, ...update })),
    [],
  );

  const updateExtras = useCallback(
    (update: Partial<IExtraFields>) =>
      setState((current) => ({ ...current, extras: { ...current.extras, ...update } })),
    [],
  );

  const [allBeneficiaries, setAllBeneficiaries] = useState<ITransferBeneficiary[]>([]);
  const [otherTransferType, setOtherTransferType] = useState<BankTypeKeys>();
  const [banksMappedList, setBanksMappedList] = useState<IBanksMappedList>({ INSTANT: [], NON_INSTANT: [] });
  const [loadingData, setLoadingData] = useState(true);
  const [shouldFindInitBen, setShouldFindInitBen] = useState(false);

  const [isConfirmationOpen, setConfirmationOpen] = useState(false);
  const [previewData, setPreviewData] = useState<ITransactionField[]>([]);
  const [errorMsg, setErrorMsg] = useState<string>('');
  const [successMsg, setSuccessMsg] = useState<string>('');
  const [transactionId, setTransactionId] = useState<number>(NaN);
  const [toggleSchedule, setToggleSchedule] = useState<boolean | null>(null);

  const [simpleWarning, setSimpleWarning] = useState<string>();
  const showStatusModal = submitStatus === AxiosStatus.SUCCESS || submitStatus === AxiosStatus.FAILURE;

  let mainForm!: FormApi<IFormValues, Partial<IFormValues>>;

  const validate = useCallback((values: IFormValues) => validationCallback<unknown>(schema)(values), []);

  const lookUpAccount = useCallback(
    (accountNumber: string, bankCode: string, bankName: string, sourceAccount: string) => {
      updateState({ isLookingUp: true });
      updateExtras({ destinationAccountName: undefined, destinationIsExistingBen: false, beneficiaryId: undefined });
      lookupOtherAccount(accountNumber, bankCode, sourceAccount)
        .then((data: IAccountLookup) => {
          updateExtras({
            destinationAccountName: data.name,
            beneficiaryId: data.beneficiaryId,
            destinationBankName: bankName,
            destinationBankCode: bankCode,
          });
        })
        .catch((error) => {
          console.error(error);
        })
        .finally(() => updateState({ isLookingUp: false }));
    },
    [updateExtras, updateState],
  );

  const location = useLocation<ITransferBenLocationData & { initType: string }>();
  const history = useHistory();
  const handleOnSuccess = () => {
    history.push(routePaths.dashboard);
  };

  useEffect(() => {
    setLoadingData(true);
    const fetchInstantBanks = () =>
      api
        .get('transfer/otherbank/institutions', {}, true, false)
        .then((data: IGetBankInstitutions) => {
          const instantMapper = (nBank: IInstantBank) => ({
            name: nBank.name,
            code: nBank.code,
          });
          const banks: IBank[] = data.banks.map(instantMapper);

          setBanksMappedList((prevList) => ({ ...prevList, INSTANT: banks }));
        })
        .catch((e) => console.error(e));
    const fetchNonInstantBanks = () =>
      api
        .get('reference/banks', {}, true, false)
        .then((data: IGetNonInstantBanks) => {
          const nonInstantMapper = (nBank: INonInstantBank): IBank => ({
            name: nBank.BankName,
            code: nBank.BankSortCode,
          });
          const newBanks: IBank[] = data.banks.map(nonInstantMapper);
          setBanksMappedList((prevList) => ({ ...prevList, NON_INSTANT: newBanks }));
        })
        .catch((e) => console.error(e));
    Promise.all([
      fetchInstantBanks(),
      fetchNonInstantBanks(),
    ]).finally(() => {
      setLoadingData(false);
    });
  }, []);
  // useEffect(() => {
  //   if (allBeneficiaries && allBeneficiaries.length > 0) {
  //     updateState({
  //       transferBeneficiaries: allBeneficiaries.filter((b) => {
  //         const isNonInstantType = b.transferType === 'NON_INSTANT_INTERBANK';
  //         const isInstantType = b.transferType === 'INSTANT_INTERBANK';
  //         if (isNonInstantType && otherTransferType) {
  //           return otherTransferType === 'INSTANT' ? !!b.instantId : !b.instantId;
  //         }
  //         return (
  //           isInstantType && otherTransferType && (otherTransferType === 'NON_INSTANT' ? !!b.instantId : !b.instantId)
  //         );
  //       }),
  //     });
  //   }
  // }, [updateState, allBeneficiaries, otherTransferType]);
  useEffect(() => {
    if (!location.state || loadingData) return;

    const { destinationAccountNo, destinationBankCode, initType } = location.state;

    let initOtherTransferType = initType || '';
    const { INSTANT, NON_INSTANT } = banksMappedList;
    const instantBank = INSTANT.find((bank) => bank.code === destinationBankCode);
    const nonInstantBank = NON_INSTANT.find((bank) => bank.code === destinationBankCode);
    if (instantBank) {
      initOtherTransferType = 'INSTANT';
    } else if (nonInstantBank) {
      initOtherTransferType = 'NON_INSTANT';
    }

    setTimeout(() => {
      mainForm.change('destinationAccountNo', destinationAccountNo);
      mainForm.change('transferType', initOtherTransferType as BankTypeKeys);
      mainForm.change('destinationBankCode', destinationBankCode);
      setShouldFindInitBen(true);
    }, 100);
  }, [loadingData, mainForm, location.state, banksMappedList]);

  const openConfirmation = () => setConfirmationOpen(true);
  const closeConfirmation = () => {
    if (submitStatus === AxiosStatus.SUCCESS) {
      handleOnSuccess();
    } else {
      setConfirmationOpen(false);
    }
  };
  const onCloseStatusModal = () => {
    if (submitStatus === AxiosStatus.SUCCESS) {
      handleOnSuccess();
    }
    setSubmitStatus(AxiosStatus.DEFAULT);
  };

  const validateValuesAndPrompt = useCallback(
    (values: IFormValues) => {
      if (values.transferType === 'INSTANT' && !state.extras.destinationAccountName) {
        setSimpleWarning(warnings.ACCOUNT_NOT_VERIFIED);
        return false;
      }

      return true;
    },
    [state.extras.destinationAccountName],
  );

  const onRejectedSubmit = useCallback((error: IApiResponse) => {
    console.error(error || NETWORK_REQUIRED);
    setErrorMsg(error.description || NETWORK_REQUIRED);
    setSubmitStatus(AxiosStatus.FAILURE);
    closeConfirmation();
  }, []);

  const onSubmit = (values: IFormValues) => {
    if (!validateValuesAndPrompt(values)) {
      return;
    }

    const account = accounts.find((a) => a.accountNumber === values.sourceAccount);

    if (Number(account?.availableBalance) < Number(values.amount)) {
      setSubmitStatus(AxiosStatus.FAILURE);
      setErrorMsg('Transfer failed.\nInsufficient funds to complete the transaction.');
      return;
    }
    const readyValues = {
      sourceAccount: values.sourceAccount,
      amount: values.amount,
      destinationAccountNo: values.destinationAccountNo,
      narration: values.narration,
      transactionFee: state.extras.charge ? state.extras.charge : 0,
      transferType: values.transferType,
      frequency: values.frequency === 'specialDate' ? 'SPECIAL DATE' : values.frequency?.toLocaleUpperCase(),
      startDate: values.startDate || '',
      destinationAccountName: state.extras.destinationAccountName,
      destinationBankName: state.extras.destinationBankName,
    } as Record<string, string | number>;

    if (state.extras.charge) {
      readyValues.transactionFee = state.extras.charge ? state.extras.charge : 0;
      readyValues.totalAmount = Number(readyValues.amount) + Number(readyValues.transactionFee);
      readyValues.transactionAmount = readyValues.amount;
      delete readyValues.amount;
    }
    const newSortArrays = values.scheduleTransfer ? sortWithScheduleArrays : sortArrays;

    setPreviewData(getTransactionsArray(readyValues, undefined, undefined, newSortArrays));

    setSubmitStatus(AxiosStatus.DEFAULT);
    setSubmitStatus(AxiosStatus.PENDING);

    api
      .post('/customer/otp/generate')
      .then(() => {
        setSubmitStatus(AxiosStatus.DEFAULT);
        openConfirmation();
      })
      .catch((data) => {
        onRejectedSubmit(data);
      });
  };
  const onConfirm = useCallback(
    (values: IFormValues) => (otp: string, saveBeneficiary: boolean) => {
      setErrorMsg('');
      setSubmitStatus(AxiosStatus.PIN_PENDING);

      return validateOtpPin(otp)
        .then(() => {
          setSubmitStatus(AxiosStatus.PENDING);

          const sourceAccount = accounts.find((a) => a.accountNumber === values.sourceAccount);

          const requestBody = {
            ...state.extras,
            ...values,
            password: otp,
            passwordType: 'OTP',
            currencyCode: sourceAccount?.currency,
            sourceAccountType: sourceAccount?.accountType || DEFAULT_ACCOUNT_TYPE,
            saveBen: saveBeneficiary,
            transType: values.transferType === 'INSTANT' ? 'INSTANT-TRANSFER-TO-OTHER-BANK' : 'OTHERBANK-TRANSFER',
            isSchedule: values.scheduleTransfer,
          };
          delete requestBody.scheduleTransfer;
          const isInstantTransfer = values.transferType === 'INSTANT';

          if (requestBody.amount) requestBody.amount = +requestBody.amount;
          if (requestBody.beneficiaryId === undefined) delete requestBody.beneficiaryId;
          api
            .post(isInstantTransfer ? 'transfer/instantotherbank' : 'transfer/otherbank', requestBody)
            .then((response: ITransferResponse) => {
              setSuccessMsg(response.description || 'Transfer Completed Successfully!');
              closeConfirmation();
              setSubmitStatus(AxiosStatus.SUCCESS);
              setTransactionId(response.transactionId);
            })
            .catch((error: IApiResponse) => {
              console.error(error);
              setErrorMsg(error.description || 'Transfer failed.\nUnable to complete the transaction.');
              setSubmitStatus(AxiosStatus.FAILURE);
              closeConfirmation();
            });
        })
        .catch((error: IOTPError) => {
          setOTPError(error);
          handlePinError(setSubmitStatus, setErrorMsg);
        });
    },
    [accounts, state.extras],
  );

  const onGetReceipt = useCallback(() => getReceipt(transactionId), [transactionId]);

  const onResend = () => api.post('/customer/otp/generate');

  useEffect(() => {
    if (!otherTransferType) {
      updateState({ banks: [] });
      return;
    }

    updateState({ banks: banksMappedList[otherTransferType] });
  }, [banksMappedList, otherTransferType, updateState]);

  const findBeneficiaryName = useCallback(
    (values: IFormValues) => {
      const { destinationAccountNo, destinationBankCode, sourceAccount } = values || {};
      if (
        !destinationAccountNo ||
        (destinationAccountNo === state.lastLookedUpAccount &&
          destinationBankCode !== undefined &&
          destinationBankCode === state.lastLookedUpBankCode)
      ) {
        return;
      }

      const beneficiary = state.transferBeneficiaries.find((ben) => ben.accountNumber === destinationAccountNo);
      const beneficiaryName = beneficiary?.name;
      let updateLastLookedUp = false;
      const selectedBank = state.banks.find((b) => b.code === destinationBankCode);
      if (!beneficiaryName) {
        if (!selectedBank) {
          updateExtras({
            destinationAccountName: 'Selected bank is not valid.',
            destinationIsExistingBen: false,
            beneficiaryId: undefined,
          });
          return;
        }
        if (values.transferType === 'NON_INSTANT') {
          updateExtras({
            destinationAccountName: values.beneficiaryName,
            destinationIsExistingBen: false,
            beneficiaryId: undefined,
          });
          return;
        }

        const lookBankCode = selectedBank.code;
        const lookBankName = selectedBank.name;

        lookUpAccount(destinationAccountNo, lookBankCode, lookBankName, sourceAccount);
        updateLastLookedUp = true;
      } else if (beneficiary && beneficiaryName) {
        updateExtras({
          destinationAccountName: beneficiaryName,
          destinationBankName: selectedBank?.name,
          destinationBankCode: selectedBank?.code,
          destinationIsExistingBen: true,
          beneficiaryId: beneficiary.instantId ? +beneficiary.instantId : undefined,
        });
        updateLastLookedUp = true;

        if (values.transferType === 'NON_INSTANT' && beneficiaryName !== values.beneficiaryName) {
          mainForm.change('beneficiaryName', beneficiaryName);
        }
      }
      if (updateLastLookedUp) {
        updateState({ lastLookedUpAccount: destinationAccountNo, lastLookedUpBankCode: destinationBankCode });
      }
    },
    [
      state.lastLookedUpAccount,
      state.lastLookedUpBankCode,
      state.transferBeneficiaries,
      lookUpAccount,
      updateExtras,
      updateState,
      mainForm,
      state.banks,
    ],
  );

  const handleSelectBank = useCallback(
    (bankCode: string) => {
      const selectedBank = state.banks.find((b) => b.code === bankCode);
      if (selectedBank) {
        updateExtras({
          destinationBankName: selectedBank.name,
          destinationBankCode: selectedBank.code,
        });
      }
    },
    [state.banks, updateExtras],
  );

  const onChangeForm = useCallback(
    ({ dirty, values }: FormState<IFormValues>) => {
      if (dirty) {
        handleChangeForm();
      }

      setOtherTransferType(values.transferType);

      if (
        values.transferType === 'NON_INSTANT' &&
        values.beneficiaryName?.trim() &&
        values.beneficiaryName !== state.extras.destinationAccountName
      ) {
        updateExtras({ destinationAccountName: values.beneficiaryName });
      }
    },
    [updateExtras, state.extras.destinationAccountName],
  );

  const fetchBeneficiaries = useCallback(
    (values: IFormValues) => {
      const requestBody = {
        transferType: values.transferType === 'INSTANT' ? 'INSTANT_INTERBANK' : 'NON_INSTANT_INTERBANK',
        sourceAccount: values.sourceAccount,
      };
      api
        .post('beneficiary/transfer/otherBank', requestBody)
        .then((response: IGetTransferBeneficiaries) => {
          setAllBeneficiaries(response.beneficiaries);
          updateState({
            transferBeneficiaries: response.beneficiaries });
        })
        .catch((error) => {
          console.error(error);
        });
    },
    [updateState],
  );

  useEffect(() => {
    if (allBeneficiaries && allBeneficiaries.length > 0) {
      updateState({ transferBeneficiaries: allBeneficiaries });
    }
  }, [updateState, allBeneficiaries]);

  const getTransactionCharge = useCallback(
    (amount, type) => {
      updateExtras({ charge: undefined });
      const transType =
        type === 'NON_INSTANT' ? TransactionType.NON_INSTANT_INTERBANK : TransactionType.INSTANT_INTERBANK;
      getTransferCharge(amount, transType)
        .then((response: ITransferCharge) => updateExtras({ charge: response.charge }))
        .catch((err) => console.error(err));
    },
    [updateExtras],
  );

  const actions = useMemo<ITransferActions>(
    () => ({
      getTransactionCharge,
      findBeneficiaryName,
      handleSelectBank,
      fetchBeneficiaries,
    }),
    [getTransactionCharge, findBeneficiaryName, handleSelectBank, fetchBeneficiaries],
  );
  useEffect(() => {
    if (loadingData || !shouldFindInitBen || !mainForm) return;
    findBeneficiaryName(mainForm.getState().values);
    setShouldFindInitBen(false);
  }, [loadingData, shouldFindInitBen, mainForm, findBeneficiaryName]);

  const closeSimpleWarning = useCallback(() => setSimpleWarning(undefined), []);

  const onRetryConfirm = () => setSubmitStatus(AxiosStatus.DEFAULT);
  const classes = useStyles();

  const dateFilterChange = (name: string, value?: string | null | undefined) => {
    mainForm.change('startDate', value as string);
  };

  const handleClearDateInput = (name: string, value?: string | null | undefined) => {
    mainForm.change('startDate', value as string);
  };

  const toggleScheduleTransfer = () => setToggleSchedule((prev) => !prev);

  useEffect(() => {
    if (toggleSchedule == null) {
      return;
    }

    mainForm.change('scheduleTransfer', toggleSchedule);
    if (!toggleSchedule) {
      mainForm.change('frequency', '');
      mainForm.change('startDate', '');
    }
  }, [toggleSchedule]);

  return (
    <Form
      onSubmit={onSubmit}
      validate={validate}
      mutators={{ changeFieldValue, setFieldTouched }}
      render={({ handleSubmit, form, values, hasValidationErrors }) => {
        mainForm = mainForm || form;
        return (
          <form onSubmit={handleSubmit}>
            <FieldsLeveler label="Transfer From" dividerBottom>
              {renderTransferFieldsFrom(values, accounts, state, actions, form, submitting)}
            </FieldsLeveler>
            <FieldsLeveler label="Transfer To">
              {renderTransferFieldsTo(
                values,
                accounts,
                state,
                actions,
                form,
                submitting,
                toggleSchedule,
                toggleScheduleTransfer,
                classes,
                dateFilterChange,
                handleClearDateInput,
              )}
            </FieldsLeveler>
            <SubmitLeveler>
              <Button
                type="submit"
                disabled={
                  submitting || state.isLookingUp || !state.extras.destinationAccountName || hasValidationErrors
                }
                fullWidth
              >
                {submitting ? <CircularProgress size={20} color="inherit" /> : 'Make Transfer'}
              </Button>
            </SubmitLeveler>
            <OTPConfirmationModal
              open={isConfirmationOpen}
              data={previewData}
              onClose={closeConfirmation}
              onResend={onResend}
              OTPPinError={OTPError}
              onSubmit={onConfirm(values)}
              submitStatus={submitStatus}
              onSuccess={handleOnSuccess}
              onRetry={onRetryConfirm}
              getReceipt={onGetReceipt}
              successText={successMsg}
              failureText={errorMsg}
              askSaveBeneficiary
              {...(state.extras.destinationIsExistingBen
                ? { disableSaveBeneficiary: true, defaultSaveBeneficiary: false }
                : {})}
            />
            <StatusModal
              open={showStatusModal}
              status={submitStatus === AxiosStatus.SUCCESS ? 'success' : 'failure'}
              onClose={onCloseStatusModal}
              onRetry={onCloseStatusModal}
              onRetryText="Close"
            >
              {submitStatus === AxiosStatus.SUCCESS ? successMsg : (
                <div style={{ whiteSpace: 'pre-line' }}>{errorMsg}</div>
              )}
            </StatusModal>
            <FormSpy subscription={{ values: true, dirty: true }} onChange={onChangeForm} />
            <WarningModal open={!!simpleWarning} onClose={closeSimpleWarning} cancelName="Close">
              {simpleWarning}
            </WarningModal>
          </form>
        );
      }}
    />
  );
};

export default ToAnotherBank;
